import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { recaptchaSiteKey } from "env";
import LoadingButton from "components/LoadingButton";
import PasswordInput from "components/PasswordInput";
import ErrorMessage from "components/ErrorMessage";
import RecaptchaField, { RecaptchaFieldRef } from "components/RecaptchaField";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import LinkButton from "components/LinkButton";
import { loginWithPassword, approveDeviceCode, isRecaptchaRequiredError, isServiceUnavailableError } from "services/auth.service";
import { setUserData, getFlowState, setJwtTokens } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import { useClearStorageOnQueryParam } from "hooks/useClearStorageOnQueryParam";
import { isValidEmail } from "utils/validators";
import { postAuthTokenToNative } from "utils/nativeBridge";
import TextInput from "components/TextInput";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStatus, DeviceGrantStep } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const DeviceGrantPasswordLogin = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useClearStorageOnQueryParam();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_DEVICE_PASSWORD_LOGIN_SCREEN);
  const [formData, setFormData] = useState({
    email: getFlowState(configId).email || "",
    password: "",
  });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const loginStartedAt = useRef<number | null>(null);
  const recaptchaRef = useRef<RecaptchaFieldRef>(null);
  const enableRecaptcha = loginPageConfig?.enableRecaptcha ?? false;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !isValidEmail(formData.email)) {
      setErrorMessage(text.deviceGrant.passwordLogin.invalidEmail);
      return;
    }
    if (!formData.password) {
      setErrorMessage(text.deviceGrant.passwordLogin.passwordRequired);
      return;
    }
    if (enableRecaptcha && !recaptchaToken) {
      setErrorMessage(text.deviceGrant.passwordLogin.recaptchaRequired);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    trackEvent(AnalyticsEvent.DEVICE_PASSWORD_LOGIN_STARTED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.STARTED });
    loginStartedAt.current = Date.now();
    try {
      const data = await loginWithPassword({
        loginId: formData.email,
        password: formData.password,
        recaptchaToken,
        enableRecaptcha,
        url: loginPageConfig?.urls?.login_with_password,
      });
      if (data.changePasswordId) {
        appNavigate(`/device-grant-flow/password/force-change/${data.changePasswordId}`);
        return;
      }
      setUserData(data);
      const { user, ...tokens } = data;
      setJwtTokens(tokens);
      trackEvent(AnalyticsEvent.DEVICE_PASSWORD_LOGIN_SUCCEEDED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.SUCCEEDED });
      if (loginStartedAt.current) {
        trackEvent(AnalyticsEvent.DEVICE_TIME_TO_LOGIN, { duration_ms: Date.now() - loginStartedAt.current });
      }
      const userCode = sessionStorage.getItem("device_grant_user_code") ?? undefined;
      await approveDeviceCode({
        user_code: userCode,
        access_token: data.token,
        url: loginPageConfig?.urls?.approve_device_code,
      });
      trackEvent(AnalyticsEvent.APPROVE_DEVICE_CODE_SUCCEEDED, { step: DeviceGrantStep.APPROVE_DEVICE_CODE, status: AnalyticsStatus.SUCCEEDED });
      postAuthTokenToNative({
        token: data.token,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/device-grant-flow/complete`, {});
    } catch (err) {
      setErrorMessage(
        isRecaptchaRequiredError(err)
          ? text.deviceGrant.passwordLogin.recaptchaRequired
          : isServiceUnavailableError(err)
          ? text.common.serviceUnavailable
          : text.deviceGrant.passwordLogin.loginFailed
      );
      trackEvent(AnalyticsEvent.DEVICE_PASSWORD_LOGIN_FAILED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.FAILED });
      // The spent token can't be reused, but the widget still shows as
      // checked — reset it so the user can re-solve immediately instead of
      // waiting for it to expire on its own.
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
      setRecaptchaToken("");
    }
  };

  return (
    <>
      <PageMeta name="Device Grant Password Login" />
      <PageTitle>{text.deviceGrant.passwordLogin.title}</PageTitle>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <TextInput
        type="email"
        label={text.deviceGrant.passwordLogin.emailLabel}
        value={formData.email}
        placeholder={text.deviceGrant.passwordLogin.emailPlaceholder}
        onChange={(e) => onChange(e, "email")}
        disabled={loading}
      />
      <PasswordInput
        label={text.deviceGrant.passwordLogin.passwordLabel}
        value={formData.password}
        placeholder={text.deviceGrant.passwordLogin.passwordPlaceholder}
        onChange={(e) => onChange(e, "password")}
        disabled={loading}
      />

      {enableRecaptcha && (
        <RecaptchaField
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={(token) => setRecaptchaToken(token ?? "")}
          onError={(message) => setErrorMessage(message)}
          theme="dark"
          size="normal"
          tabIndex={0}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "0.5rem" }}>
        <LinkButton
          type="button"
          disabled={loading}
          onClick={() =>
            appNavigate(`/device-grant-flow/forgot-password`, {
              state: { email: formData.email },
            })
          }
          sx={{ fontSize: "0.75rem" }}
        >
          {text.deviceGrant.passwordLogin.forgotPasswordLink}
        </LinkButton>
      </Box>
      <LoadingButton
        loading={loading}
        onClick={handleLogin}
        disabled={loading}
        sx={{
          width: "100%",
          height: "3.5rem",
          mb: "1rem",
          mt: "1.5rem",
        }}
      >
        {text.deviceGrant.passwordLogin.loginButton}
      </LoadingButton>
      <Box sx={{ textAlign: "center" }}>
        <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
          {text.deviceGrant.passwordLogin.troubleLoggingIn}
        </Typography>
        <LinkButton
          onClick={() => appNavigate(`/device-grant-flow/login`)}
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {text.deviceGrant.passwordLogin.loginWithCodeButton}
        </LinkButton>
      </Box>
    </>
  );
};

export default DeviceGrantPasswordLogin;
